import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { spotifyTrack, getUtm } from "../lib/tracking";

const inputCls =
  "h-14 w-full rounded-2xl border border-forest/15 bg-white px-5 font-manrope text-sm text-forest outline-none transition-shadow duration-300 placeholder:text-forest/40 focus:ring-2 focus:ring-fresh";

const EMPTY_FORM = { name: "", email: "", phone: "", role: "driver" };

const RolePicker = ({ role, onPick }) => (
  <div data-testid="recruitment-role" className="grid grid-cols-2 gap-2 rounded-2xl border border-forest/15 bg-mist p-2">
    {["driver", "picker"].map((r) => (
      <button
        key={r}
        type="button"
        data-testid={`recruitment-role-${r}`}
        onClick={() => onPick(r)}
        className={`h-12 rounded-xl font-manrope text-sm font-bold capitalize transition-colors duration-300 ${
          role === r ? "bg-forest text-bone" : "text-forest/60 hover:text-forest"
        }`}
      >
        {r}
      </button>
    ))}
  </div>
);

export const RecruitmentForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/driver-applications`, { ...form, ...getUtm() });
      spotifyTrack("lead", { type: "driver_application", category: "signup" });
      toast.success("Application received — our team will reach out within 48 hours.");
      setForm(EMPTY_FORM);
    } catch {
      toast.error("Couldn't submit your application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="recruitment-form" className="rounded-3xl bg-bone p-8 shadow-2xl md:p-10">
      <h3 className="font-syne text-2xl font-bold tracking-tight text-forest">Apply in 60 seconds</h3>
      <div className="mt-8 space-y-4">
        <input
          required
          value={form.name}
          onChange={set("name")}
          placeholder="Full name"
          data-testid="recruitment-name"
          className={inputCls}
        />
        <input
          required
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="Email address"
          data-testid="recruitment-email"
          className={inputCls}
        />
        <input
          required
          type="tel"
          value={form.phone}
          onChange={set("phone")}
          placeholder="Phone number"
          data-testid="recruitment-phone"
          className={inputCls}
        />
        <RolePicker role={form.role} onPick={(r) => setForm({ ...form, role: r })} />
        <motion.button
          type="submit"
          disabled={loading}
          data-testid="recruitment-submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-fresh font-manrope text-sm font-bold text-white transition-colors duration-300 hover:bg-forest disabled:opacity-60"
        >
          {loading ? "Submitting..." : "Apply Now"}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </motion.button>
      </div>
    </form>
  );
};
